import { FC, ReactNode } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { Header } from 'dibs-elements/exports/Header';
import { HeadingType } from 'dibs-controlled-heading/exports/HeadingLevel';
import { RelayNode } from 'dibs-ts-utils/exports/RelayNode';
import { ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';
import { trackEvent } from 'dibs-tracking';
import classNames from 'classnames';
import styles from './PopularSearchesSitemapLinksBlock.scss';
import type { PopularSearchesSitemapLinksBlock_links } from './__generated__/PopularSearchesSitemapLinksBlock_links.graphql';
import type { PopularSearchesSitemapLinksBlock_connectionLinks } from './__generated__/PopularSearchesSitemapLinksBlock_connectionLinks.graphql';

export const MOST_POPULAR_BUY_PAGES = 'most-popular-buy-pages';
export const DESCENDANT_CATEGORIES = 'descendant-categories';
export const OTHER_POPULAR_BUY_PAGES = 'other-popular-buy-pages';

export type PopularSearchesDataTnType =
    | typeof MOST_POPULAR_BUY_PAGES
    | typeof DESCENDANT_CATEGORIES
    | typeof OTHER_POPULAR_BUY_PAGES;

type Props = {
    headerElement: HeadingType;
    title: ReactNode;
    dataTn: PopularSearchesDataTnType;
    links: PopularSearchesSitemapLinksBlock_links | null;
    connectionLinks: PopularSearchesSitemapLinksBlock_connectionLinks | null;
};

type LinksType =
    | PopularSearchesSitemapLinksBlock_links
    | Array<RelayNode<PopularSearchesSitemapLinksBlock_connectionLinks> | undefined>;

type LinkType = ArrayElement<LinksType>;

const POPULAR_SEARCH_EVENT_LABEL = {
    [MOST_POPULAR_BUY_PAGES]: 'popular link',
    [DESCENDANT_CATEGORIES]: 'category link',
    [OTHER_POPULAR_BUY_PAGES]: 'other link',
};

const PopularSearchesSitemapLinksBlockComponent: FC<Props> = ({
    headerElement,
    title,
    dataTn,
    links,
    connectionLinks,
}) => {
    let linksToRender: LinksType = [];

    if (links?.length) {
        linksToRender = links;
    }

    if (connectionLinks?.edges?.length) {
        linksToRender = connectionLinks.edges.map(link => link?.node);
    }

    if (!linksToRender.length) {
        return null;
    }

    return (
        <div className={styles.linksBlock} data-tn={dataTn}>
            <div className={styles.linksBlockHeader}>
                <Header title={title} htmlElementType={headerElement} />
            </div>
            <nav>
                <ol className="rowFlex">
                    {(linksToRender as Array<LinkType>).map(link => {
                        if (!link) {
                            return null;
                        }
                        return (
                            <li className={classNames('colLg3', styles.link)} key={link.path}>
                                <SeoLink
                                    dataTn={`${dataTn}-link`}
                                    linkData={link}
                                    onClick={() =>
                                        trackEvent({
                                            category: 'promo interaction',
                                            action: 'popular searches page link click',
                                            label: POPULAR_SEARCH_EVENT_LABEL[dataTn],
                                        })
                                    }
                                >
                                    {link.text}
                                </SeoLink>
                            </li>
                        );
                    })}
                </ol>
            </nav>
        </div>
    );
};

export const PopularSearchesSitemapLinksBlock = createFragmentContainer(
    PopularSearchesSitemapLinksBlockComponent,
    {
        links: graphql`
            fragment PopularSearchesSitemapLinksBlock_links on LinkData @relay(plural: true) {
                text
                path
                ...SeoLink_linkData
            }
        `,
        connectionLinks: graphql`
            fragment PopularSearchesSitemapLinksBlock_connectionLinks on PopularBuyPagesConnection {
                edges {
                    node {
                        text
                        path
                        ...SeoLink_linkData
                    }
                }
            }
        `,
    }
);
